import { useEffect, useRef } from 'react';

import { useKaiCoachService } from './useKaiCoachService';

interface UsePrepareKaiCoachSessionParams {
  // Non-empty only for a thread of a SPLIT session; unsplit sessions have one
  // thread and nothing to share, so they are never prepared.
  activityUid: string;
  courseId: string;
  // False while no open thread is selected.
  enabled: boolean;
  moduleId: string;
  sessionUid: string;
}

// Fire-and-forget: when a thread of an open split session becomes active, ask
// the backend to build the student's shared context root for that session so
// it is ready before their first message. Never awaited and never shown. At
// most one request per session per mount; a failed request is retried the next
// time a thread of that session is activated, a completed one is not.
export function usePrepareKaiCoachSession({
  activityUid,
  courseId,
  enabled,
  moduleId,
  sessionUid,
}: UsePrepareKaiCoachSessionParams): void {
  const { service, userId } = useKaiCoachService();
  // Sessions with a request in flight or completed this mount.
  const attemptedRef = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled || !service || !userId || !courseId || !moduleId || !sessionUid || !activityUid) {
      return;
    }
    if (attemptedRef.current.has(sessionUid)) {
      return;
    }
    attemptedRef.current.add(sessionUid);
    service
      .prepareSession({ courseUid: courseId, moduleUid: moduleId, sessionUid, userId })
      .catch(() => {
        // The send path copes without a root; allow another attempt later.
        attemptedRef.current.delete(sessionUid);
      });
  }, [activityUid, courseId, enabled, moduleId, service, sessionUid, userId]);
}
